import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Slider,
  Box,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { styles } from "../../pages/Tracks/styles";
import {
  ArtWorkAndCollaboratorsContainer,
  AudioPlayerTime,
  CloseButton,
  OuterPlayerContainer,
  PlayerContainer,
} from "./styles";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";
import { AudioPlayerMainContainer, audioPlayerStyles } from "./styles";
import { AudioPlayerProps } from "./types";
import { useAuth0 } from "@auth0/auth0-react";
import { Collaborators } from "./Collaborators";
import CloseIcon from "@mui/icons-material/Close";
import AddCommentModal from "../Modal/AddCommentModal";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { handleGetTrackComments } from "../../services/commentsService";
import {
  AudioComment,
  GetTrackTimeStampCommentsResponse,
  Role,
} from "../../proto/api_pb";
import TrackComments from "../Modal/TrackComments";
import { formatTime } from "./helper";

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  trackToPlay,
  isPlayButtonClicked,
  versionToPlay,
  isTrackForPlay,
  audioDetails,
  setTrackToPlay,
  setIsPlayButtonClicked,
  setVersionToPlay,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const { isAuthenticated } = useAuth0();
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [allComment, setAllComments] = useState<AudioComment>();
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const { enterprise, isEnterpriseAccount } = useContext(UserDetailsContext);
  const { userId } = useContext<AuthContextInterface>(AuthContext);

  const { getValidAccessToken } = useGetValidAccessToken();
  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setDuration(audio.duration);
      if (
        (isPlayButtonClicked && trackToPlay?.isPlaying) ||
        versionToPlay?.isPlaying
      ) {
        audio.play();
        setIsPlaying(true);
      }
      setIsAudioLoading(false);
    }
  }, [isPlayButtonClicked, trackToPlay?.isPlaying, versionToPlay?.isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);

      if (
        (isPlayButtonClicked && trackToPlay?.isPlaying) ||
        versionToPlay?.isPlaying
      ) {
        audio.play();
        setIsPlaying(true);
      }

      return () => {
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [
    handleLoadedMetadata,
    isPlayButtonClicked,
    trackToPlay?.isPlaying,
    versionToPlay?.isPlaying,
  ]);

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
        if (trackToPlay) {
          setTrackToPlay((prev) => {
            return {
              track: trackToPlay?.track,
              url: trackToPlay?.url,
              isPlaying: false,
            };
          });
        } else if (setVersionToPlay) {
          setVersionToPlay((prev) => {
            return {
              track: versionToPlay?.track,
              isPlaying: false,
              isSelected: false,
            };
          });
        }
      } else {
        audio.play();
        if (trackToPlay) {
          setTrackToPlay((prev) => {
            return {
              track: trackToPlay?.track,
              url: trackToPlay?.url,
              isPlaying: true,
            };
          });
        } else if (setVersionToPlay) {
          setVersionToPlay((prev) => {
            return {
              track: versionToPlay?.track,
              isPlaying: true,
              isSelected: true,
            };
          });
        }
      }
      setIsPlaying(!isPlaying);
    }
  }, [
    isPlaying,
    setTrackToPlay,
    setVersionToPlay,
    trackToPlay,
    versionToPlay?.track,
  ]);

  useEffect(() => {
    const audio = audioRef.current;
    if ((trackToPlay?.isPlaying || versionToPlay?.isPlaying) && audio) {
      audio.play();
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
      audio?.pause();
    }
  }, [isPlaying, trackToPlay?.isPlaying, versionToPlay?.isPlaying]);

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
    }
  };

  const handleSeek = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Array.isArray(newValue) ? newValue[0] : newValue;
      setCurrentTime(audio.currentTime);
    }
  };

  const imageUrl = isTrackForPlay
    ? trackToPlay?.track?.getImageUrl()
    : audioDetails?.image;

  const handleGetComments = useCallback(async () => {
    const token = await getValidAccessToken();
    const res = await handleGetTrackComments(
      token,
      isTrackForPlay
        ? trackToPlay?.track?.getId() ?? ""
        : versionToPlay?.track?.getTrackId() ?? "",
      isTrackForPlay
        ? trackToPlay?.track?.getTrackVersionId() ?? ""
        : versionToPlay?.track?.getId() ?? ""
    );
    if (res instanceof GetTrackTimeStampCommentsResponse) {
      setAllComments(res.getAudioComment());
    }
  }, [
    getValidAccessToken,
    isTrackForPlay,
    trackToPlay?.track,
    versionToPlay?.track,
  ]);

  const refreshComments = async () => {
    await handleGetComments();
  };

  const handlePauseTrack = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      if (trackToPlay) {
        setTrackToPlay((prev) => ({
          track: trackToPlay?.track,
          url: trackToPlay?.url,
          isPlaying: false,
        }));
      } else if (setVersionToPlay) {
        setVersionToPlay((prev) => ({
          track: versionToPlay?.track,
          isPlaying: false,
          isSelected: false,
        }));
      }
      setIsPlaying(false);
    }
  };
  const handlePlayTrack = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.play();
      setTrackToPlay((prev) => ({
        track: trackToPlay?.track,
        url: trackToPlay?.url,
        isPlaying: true,
      }));
      setIsPlaying(true);
    }
  };
  useEffect(() => {
    setAllComments(new AudioComment());
    if (trackToPlay || versionToPlay) handleGetComments();
  }, [handleGetComments, trackToPlay, versionToPlay]);

  return (
    <>
      <audio
        style={{ display: "none" }}
        ref={audioRef}
        src={
          isTrackForPlay
            ? trackToPlay?.url
            : versionToPlay?.track?.getTrackUrl()
        }
        onLoadStart={() => setIsAudioLoading(true)}
        onCanPlay={() => setIsAudioLoading(false)}
        onLoadedData={() => setIsAudioLoading(false)}
      />
      {isPlayButtonClicked && (
        <AudioPlayerMainContainer
          style={
            !isAuthenticated
              ? {
                  width: "100%",
                }
              : {}
          }
        >
          <Box sx={audioPlayerStyles.imageTitleContainer}>
            <ArtWorkAndCollaboratorsContainer>
              <img
                style={styles.image}
                src={
                  imageUrl && imageUrl.includes("http")
                    ? imageUrl
                    : isEnterpriseAccount
                    ? enterprise?.image
                    : "/assets/images/logo512.png"
                }
                alt=""
              />
            </ArtWorkAndCollaboratorsContainer>
            <Typography sx={{ minWidth: "100px" }}>
              {isTrackForPlay
                ? trackToPlay?.track?.getTitle()
                : audioDetails?.title}
            </Typography>
          </Box>
          <OuterPlayerContainer>
            <ArtWorkAndCollaboratorsContainer>
              {trackToPlay?.track && (
                <Collaborators
                  collaborators={trackToPlay?.track.getCollaboratorsList()}
                  isGridView={true}
                />
              )}
            </ArtWorkAndCollaboratorsContainer>
            <Box sx={{ display: "flex" }}>
              {isEnterpriseAccount &&
                (enterprise?.role !== Role.ROLE_ARTIST.toString() ||
                  trackToPlay?.track
                    ?.getCollaboratorsList()
                    .some(
                      (collaborator) => collaborator.getUserId() === userId
                    )) && (
                  <AddCommentModal
                    trackId={
                      isTrackForPlay
                        ? trackToPlay?.track?.getId() ?? ""
                        : versionToPlay?.track?.getTrackId() ?? ""
                    }
                    versionId={
                      isTrackForPlay
                        ? trackToPlay?.track?.getTrackVersionId() ?? ""
                        : versionToPlay?.track?.getId() ?? ""
                    }
                    commentTime={currentTime}
                    refreshComments={refreshComments}
                    pauseTrack={handlePauseTrack}
                    playTrack={handlePlayTrack}
                  />
                )}
              <TrackComments allComment={allComment} />
            </Box>
            <PlayerContainer>
              {isAudioLoading ? (
                <CircularProgress sx={{ color: "white" }} size={22} />
              ) : (
                <IconButton onClick={handlePlayPause}>
                  {isPlaying ? (
                    <PauseCircleOutlineOutlinedIcon sx={{ fontSize: 30 }} />
                  ) : (
                    <PlayCircleOutlineIcon sx={{ fontSize: 30 }} />
                  )}
                </IconButton>
              )}
            </PlayerContainer>
            {!isSmallScreen && (
              <Slider
                value={currentTime}
                min={0}
                max={duration}
                onChange={handleSeek}
                sx={styles.trackSlider}
              />
            )}
            <AudioPlayerTime>
              {formatTime(currentTime)} / {formatTime(duration)}
            </AudioPlayerTime>
          </OuterPlayerContainer>
          <Tooltip title="Close">
            <CloseButton
              sx={isSmallScreen ? { marginRight: -2 } : {}}
              onClick={() => {
                const audio = audioRef.current;
                if (audio) {
                  audio.pause();
                  if (isTrackForPlay) {
                    setTrackToPlay((prev) => {
                      return {
                        track: trackToPlay?.track,
                        url: trackToPlay?.url,
                        isPlaying: false,
                      };
                    });
                  } else if (setVersionToPlay) {
                    setVersionToPlay({
                      track: versionToPlay?.track,
                      isPlaying: false,
                      isSelected: true,
                    });
                  }
                }
                setIsPlayButtonClicked(false);
              }}
            >
              <CloseIcon />
            </CloseButton>
          </Tooltip>
        </AudioPlayerMainContainer>
      )}
    </>
  );
};
